import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Shield, Umbrella, CheckCircle, AlertTriangle, User, Phone, Mail, ArrowRight, TrendingUp, Users, Car, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LifeCover = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Life Cover & Income Protection South Africa | Get a Free Quote | AutoSearch SA';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Protect your family with life cover and income protection insurance. Get free quotes for life insurance, disability cover & funeral plans in South Africa.');
  }, []);

  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    coverType: 'life',
    contactPreference: 'email',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        lead_type: leadForm.coverType,
        contact_preference: leadForm.contactPreference,
        message: `Cover type: ${leadForm.coverType}. ${leadForm.message}`
      });

      await axios.post(`${API}/track/click`, {
        action_type: 'life_cover_enquiry',
        source: 'Life Cover Page'
      });

      setSubmitted(true);
    } catch (e) {
      console.error('Lead submission failed:', e);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const coverTypes = [
    {
      id: 'life',
      icon: Heart,
      name: 'Life Cover',
      desc: 'Protect your loved ones financially if something happens to you',
      color: 'red',
      benefits: [
        'Lump sum payout to beneficiaries',
        'Cover funeral costs',
        'Pay off bond and debts',
        'Provide for children\'s education',
        'Replace lost income for family'
      ]
    },
    {
      id: 'income',
      icon: Umbrella,
      name: 'Income Protection',
      desc: 'Receive monthly income if you can\'t work due to illness or injury',
      color: 'blue',
      benefits: [
        'Monthly income while recovering',
        'Cover living expenses',
        'Pay car and bond installments',
        'Maintain lifestyle during recovery',
        'No financial stress while healing'
      ]
    },
    {
      id: 'disability',
      icon: Shield,
      name: 'Disability Cover',
      desc: 'Lump sum payment if you become permanently disabled',
      color: 'purple',
      benefits: [
        'Lump sum for permanent disability',
        'Adapt home for accessibility',
        'Pay off debts',
        'Cover medical equipment',
        'Financial independence'
      ]
    }
  ];

  const stats = [
    { icon: Car, value: '#1', label: 'Car accidents are the leading cause of disability claims in SA' },
    { icon: TrendingUp, value: 'R50k', label: 'Average monthly shortfall for disabled breadwinners' },
    { icon: Users, value: '70%', label: 'of South Africans are underinsured for life cover' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your enquiry. A life cover specialist will contact you within 24 hours to discuss your options.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            You can also contact us directly at:<br/>
            <a href="mailto:autosearchsa@gmail.com" className="text-red-600 font-medium">autosearchsa@gmail.com</a>
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Urgent Warning Banner */}
      <div className="bg-red-900 text-white py-3">
        <div className="container flex items-center justify-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span><strong>Did you know?</strong> 1 in 4 South Africans will become disabled before retirement. Are you protected?</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm font-medium mb-4">
              More Important Than Car Insurance
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Protect Your Income, Protect Your Family</h1>
            <p className="text-xl text-red-100 mb-6">
              You insure your car because it's valuable. But your ability to earn an income is worth far more. 
              What happens to your family if you can't work?
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#quote" className="px-6 py-3 bg-white text-red-600 rounded-lg font-semibold hover:bg-red-50 transition">
                Get a Free Quote
              </a>
              <a href="#why" className="px-6 py-3 bg-red-700 text-white rounded-lg font-semibold hover:bg-red-800 transition border border-red-500">
                Why Income Protection?
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Income Protection - NEW SECTION */}
      <section id="why" className="py-12 bg-white border-b">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Why Income Protection is Essential</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="font-bold text-lg text-red-800 mb-3">The Reality Check</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Car accidents are the <strong>#1 cause of disability claims</strong> in South Africa</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>The average disability lasts <strong>2.5 years</strong> - how long can you survive without income?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span><strong>70% of South Africans</strong> don't have adequate income protection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>UIF only covers <strong>45% of your salary for 6 months</strong> - then what?</span>
                  </li>
                </ul>
              </div>
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h3 className="font-bold text-lg text-blue-800 mb-3">What Income Protection Does</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Pays up to <strong>75% of your salary monthly</strong> while you recover</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Covers <strong>any illness or injury</strong> that prevents you from working</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Keeps paying your <strong>bond, car, and bills</strong> while you heal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Costs less than you think - from <strong>R150/month</strong></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 text-white py-8">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-400">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container py-12">
        {/* Why This Matters */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 mb-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">The Uncomfortable Truth About Car Ownership</h2>
              <p className="text-gray-700 mb-4">
                You wouldn't dream of driving without car insurance. But here's the thing: your car can be replaced. 
                <strong> Your income cannot.</strong>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🚗 If your car is written off:</h4>
                  <p className="text-sm text-gray-600">Insurance pays out, you buy a new car. Life goes on.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🚨 If YOU can't work:</h4>
                  <p className="text-sm text-gray-600">No income, but bond payments, school fees, and living costs don't stop.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cover Types */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Types of Cover</h2>
            <div className="space-y-6">
              {coverTypes.map((type) => (
                <div key={type.id} className={`bg-white rounded-2xl shadow-sm p-6 border-l-4 border-${type.color}-500`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 bg-${type.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <type.icon className={`w-7 h-7 text-${type.color}-600`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{type.name}</h3>
                      <p className="text-gray-600 mb-4">{type.desc}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {type.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Section */}
            <div className="bg-gray-100 rounded-2xl p-6 mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How It Works</h3>
              <p className="text-gray-600 mb-4">
                We work with leading South African insurers to find you the best life cover and income protection options. 
                Simply fill in the form and a specialist will contact you to discuss your needs and provide tailored quotes.
              </p>
              <p className="text-sm text-gray-500">
                All quotes are obligation-free. Your information is kept confidential.
              </p>
            </div>
          </div>

          {/* Lead Form */}
          <div id="quote" className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 border-2 border-red-100">
              <div className="bg-red-50 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-2xl">
                <h3 className="text-xl font-bold text-red-800">Get a Free Quote</h3>
                <p className="text-red-600 text-sm">
                  No obligation. Takes 30 seconds.
                </p>
              </div>

              {/* Bundle Promo */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-blue-800 text-sm font-medium">💡 Bundle & Save</p>
                <p className="text-blue-700 text-xs">Combine life cover, income protection & insurance for better rates!</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      required
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="John Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      required
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="082 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">I'm interested in</label>
                  <select
                    value={leadForm.coverType}
                    onChange={(e) => setLeadForm({ ...leadForm, coverType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                  >
                    <option value="life_cover">Life Cover</option>
                    <option value="income_protection">Income Protection</option>
                    <option value="disability">Disability Cover</option>
                    <option value="bundle">Bundle All (Best Rates)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Method</label>
                  <div className="flex gap-2">
                    {['email', 'call', 'whatsapp'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setLeadForm({ ...leadForm, contactPreference: method })}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition ${
                          leadForm.contactPreference === method
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                        }`}
                      >
                        {method === 'email' && '📧 Email'}
                        {method === 'call' && '📞 Call'}
                        {method === 'whatsapp' && '💬 WhatsApp'}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Get Free Quote'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Your information is secure and will only be used to provide you with a quote.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifeCover;
